import _ from 'lodash';
import { action, computed, observable, makeObservable } from 'mobx';
import {DomainObject, oneOf} from 'shared/store';
import {t} from 'shared/core';
import AttachedDocument from 'shared/stores/documents/AttachedDocument';
import NotificationRecipient from './NotificationRecipient';
import AssignmentRule from './AssignmentRule';
import DocumentAction from './DocumentAction';
import {calendarDate} from 'shared/tools';

const COLOURS = [
  '#1776F5',
  '#F64A6C',
  '#7E66E7',
  '#CB7525',
  '#80475E'
];

class CompanyDocument extends DomainObject {
  id;
  name = t('models.company_document.DEFAULT_NAME');
  signatureRequired;
  previewFile;
  documentType;
  locale;
  order;
  folderId;
  notificationRecipients = [];
  assignmentRules = [];
  documentActions = [];
  createdAt;
  completedAt;

  // PDF Template
  annotations = [];
  originalFile;

  // Rich Text Template
  richText = `<br/>`;
  lexicalState;

  actionedEmployeeIds = [];
  assignedToPrimaryEmployee;

  _relations = {
    previewFile: AttachedDocument,
    originalFile: AttachedDocument,
    documentActions: [DocumentAction],
    notificationRecipients: [NotificationRecipient],
    assignmentRules: [AssignmentRule],
    documentType: oneOf(['rich_text_template', 'pdf_template', 'static_document'])
  };

  constructor(other) {
    super();

    makeObservable(this, {
      id: observable,
      name: observable,
      signatureRequired: observable,
      previewFile: observable,
      documentType: observable,
      locale: observable,
      order: observable,
      folderId: observable,
      notificationRecipients: observable,
      assignmentRules: observable,
      documentActions: observable,
      createdAt: observable,
      completedAt: observable,
      annotations: observable,
      originalFile: observable,
      richText: observable,
      lexicalState: observable,
      actionedEmployeeIds: observable,
      assignedToPrimaryEmployee: observable,
      addRecipient: action,
      removeRecipient: action,
      addRule: action,
      removeRule: action,
      createdAtView: computed,
      completedAtView: computed,
      primaryDocumentAction: computed,
      typeView: computed,
      hasFiles: computed,
      sortedAssignmentRules: computed,
      hasExplicitEmployeeRule: computed,
      sortedDocumentActions: computed,
      selectableColors: computed
    });

    this.merge(other);
  }

  addRecipient(recipient) {
    this.notificationRecipients.push(recipient);
  }

  removeRecipient(recipient) {
    _.remove(this.notificationRecipients, {value: recipient.value});
  }

  addRule(assignee) {
    this.assignmentRules.push(new AssignmentRule({
      employee: assignee.employee,
      assigneeType: assignee.assigneeType,
      companyDocument: this,
      color: this.selectableColors[0],
      order: this.assignmentRules.length
    }));
  }

  removeRule(assigneeId) {
    _.remove(this.assignmentRules, {assignee: assigneeId});
    this.sortedAssignmentRules.forEach((rule, index) => rule.order = index);
  }

  get createdAtView() {
    return calendarDate(this.createdAt);
  }

  get completedAtView() {
    return calendarDate(this.completedAt);
  }

  get primaryDocumentAction() {
    return this.sortedDocumentActions[0];
  }

  get typeView() {
    switch (this.documentType) {
      case 'rich_text_template':
      case 'pdf_template':
      case 'static_document':
        return t(`models.company_document.document_type.${this.documentType}`);
      default:
        throw new Error(`Document type of ${this.documentType} is not supported.`);
    }
  }

  get hasFiles() {
    return !_.isEmpty(this.previewFile);
  }

  get sortedAssignmentRules() {
    return _.sortBy(this.assignmentRules, 'order');
  }

  get hasExplicitEmployeeRule() {
    return _.some(this.assignmentRules, {assigneeType: 'employee'});
  }

  get sortedDocumentActions() {
    return _.sortBy(this.documentActions, 'order');
  }

  get selectableColors() {
    const usedColorCounts = _.countBy(this.assignmentRules, 'color');
    const colorCounts = _.fromPairs(
      COLOURS.map(color => [color, usedColorCounts[color] || 0])
    );
    const minUsageCount = _.min(_.values(colorCounts));

    return COLOURS.filter(color => colorCounts[color] === minUsageCount);
  }
}

export default CompanyDocument;
